import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Product } from '@/mocks/products';

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  const { t } = useTranslation();
  const [showDetail, setShowDetail] = useState(false);

  const specs = [
    { label: t('products.card.specs.power'), value: product.power, icon: 'ri-flashlight-line' },
    { label: t('products.card.specs.colorTemp'), value: product.colorTemp, icon: 'ri-sun-line' },
    { label: t('products.card.specs.ipRating'), value: product.ipRating, icon: 'ri-shield-line' },
  ];

  return (
    <>
      <div
        className="group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-amber-200 transition-all duration-300 cursor-pointer"
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
        onClick={() => setShowDetail(true)}
      >
        <div className="relative w-full h-52 bg-gray-50 overflow-hidden">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
          {product.badge && (
            <span className="absolute top-3 left-3 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {product.badge}
            </span>
          )}
          <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <i className="ri-eye-line text-gray-700 text-sm" />
          </div>
        </div>
        <div className="p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-amber-600 font-semibold bg-amber-50 rounded-full px-2.5 py-0.5 whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>{product.category}</span>
            <span className="text-xs text-gray-400 font-mono">{product.code}</span>
          </div>
          <h3 className="text-gray-900 font-bold text-base mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>{product.name}</h3>
          <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>{product.description}</p>
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1 flex items-center gap-1 whitespace-nowrap">
              <i className="ri-flashlight-line text-amber-500 text-xs" />{product.power}
            </span>
            <span className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1 flex items-center gap-1 whitespace-nowrap">
              <i className="ri-shield-line text-amber-500 text-xs" />{product.ipRating}
            </span>
          </div>
          <button className="w-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold py-2.5 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {t('products.card.detail')}
          </button>
        </div>
      </div>

      {showDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40 backdrop-blur-sm" onClick={() => setShowDetail(false)}>
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div className="relative w-full h-64 bg-gray-50 rounded-t-3xl overflow-hidden">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
              {product.badge && (
                <span className="absolute top-4 left-4 bg-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full" style={{ fontFamily: "'Montserrat', sans-serif" }}>{product.badge}</span>
              )}
              <button onClick={() => setShowDetail(false)} className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/90 rounded-full cursor-pointer hover:bg-white transition-colors">
                <i className="ri-close-line text-gray-700 text-lg" />
              </button>
            </div>
            <div className="p-7">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <span className="text-xs text-amber-600 font-semibold bg-amber-50 rounded-full px-2.5 py-1 mb-2 inline-block" style={{ fontFamily: "'Montserrat', sans-serif" }}>{product.category}</span>
                  <h2 className="text-gray-900 text-2xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{product.name}</h2>
                </div>
                <span className="text-xs text-gray-400 font-mono bg-gray-50 px-3 py-1.5 rounded-lg whitespace-nowrap flex-shrink-0">{product.code}</span>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>{product.description}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                {specs.map((spec, i) => (
                  <div key={i} className="bg-gray-50 border border-gray-100 rounded-xl p-3">
                    <div className="w-6 h-6 flex items-center justify-center mb-1.5">
                      <i className={`${spec.icon} text-amber-500 text-sm`} />
                    </div>
                    <div className="text-gray-400 text-xs mb-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>{spec.label}</div>
                    <div className="text-gray-800 text-xs font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>{spec.value}</div>
                  </div>
                ))}
              </div>
              <div className="mb-6">
                <h4 className="text-gray-900 text-sm font-bold mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('products.card.features')}</h4>
                <ul className="space-y-2">
                  {product.features.map((feat, i) => (
                    <li key={i} className="flex items-center gap-2.5">
                      <div className="w-4 h-4 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                        <i className="ri-check-line text-amber-600 text-xs" />
                      </div>
                      <span className="text-gray-600 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="#contact" onClick={() => setShowDetail(false)} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 rounded-xl text-sm text-center transition-all duration-300 cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {t('products.card.requestQuote')}
                </a>
                <button onClick={() => setShowDetail(false)} className="flex-1 border border-gray-200 text-gray-600 hover:border-amber-400 hover:text-amber-600 font-medium py-3 rounded-xl text-sm transition-all duration-300 cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {t('products.card.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}